/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Firebase Client Configuration
 * 
 * Configures Firebase for client-side authentication.
 * Reads configuration from environment variables to support unauthenticated contexts.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

/**
 * Firebase configuration object
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId: string;
  measurementId?: string;
}

/**
 * Get Firebase configuration from environment variables
 * 
 * This reads from Vite environment variables (prefixed with VITE_) in the browser.
 * In Electron apps, it also checks for injected config from window.__FIREBASE_CONFIG__.
 * 
 * The environment variables should be set in .env files:
 * - VITE_FIREBASE_API_KEY
 * - VITE_FIREBASE_AUTH_DOMAIN
 * - VITE_FIREBASE_PROJECT_ID
 * - VITE_FIREBASE_STORAGE_BUCKET (optional)
 * - VITE_FIREBASE_MESSAGING_SENDER_ID (optional)
 * - VITE_FIREBASE_APP_ID
 * - VITE_FIREBASE_MEASUREMENT_ID (optional)
 */
function getFirebaseConfig(): FirebaseConfig | null {
  try {
    // Check if we're in a browser environment
    const isBrowser = typeof window !== 'undefined';
    
    if (!isBrowser) {
      console.warn('Firebase client config should only be accessed in browser environment');
      return null;
    }

    // Check for Electron-injected config first
    const electronConfig = (window as any).__FIREBASE_CONFIG__;
    if (electronConfig) {
      const apiKey = electronConfig.VITE_FIREBASE_API_KEY;
      const authDomain = electronConfig.VITE_FIREBASE_AUTH_DOMAIN;
      const projectId = electronConfig.VITE_FIREBASE_PROJECT_ID;
      const appId = electronConfig.VITE_FIREBASE_APP_ID;

      if (apiKey && authDomain && projectId && appId) {
        console.log('Using Firebase config from Electron injection');
        return {
          apiKey,
          authDomain,
          projectId,
          storageBucket: electronConfig.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: electronConfig.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId,
          measurementId: electronConfig.VITE_FIREBASE_MEASUREMENT_ID
        };
      }
    }

    // Fall back to Vite environment variables (for web builds)
    const apiKey = import.meta.env?.VITE_FIREBASE_API_KEY;
    const authDomain = import.meta.env?.VITE_FIREBASE_AUTH_DOMAIN;
    const projectId = import.meta.env?.VITE_FIREBASE_PROJECT_ID;
    const appId = import.meta.env?.VITE_FIREBASE_APP_ID;

    // Check required fields
    if (!apiKey || !authDomain || !projectId || !appId) {
      console.log('Firebase configuration incomplete or not provided in environment variables');
      console.log('Required: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_APP_ID');
      return null;
    }

    return {
      apiKey,
      authDomain,
      projectId,
      storageBucket: import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env?.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId,
      measurementId: import.meta.env?.VITE_FIREBASE_MEASUREMENT_ID
    };
  } catch (error) {
    console.error('Error reading Firebase config from environment:', error);
    return null;
  }
}

/**
 * Initialize Firebase app with configuration from environment variables
 */
export function initializeFirebase(): { app: FirebaseApp | null; auth: Auth | null } {
  // Return existing instances if already initialized
  if (firebaseApp && firebaseAuth) {
    return { app: firebaseApp, auth: firebaseAuth };
  }
  
  try {
    const config = getFirebaseConfig();
    
    if (!config) {
      console.log('Firebase not configured or disabled');
      return { app: null, auth: null };
    }
    
    // Initialize Firebase
    firebaseApp = initializeApp(config);
    firebaseAuth = getAuth(firebaseApp);
    
    console.log('✅ Firebase initialized successfully');
    return { app: firebaseApp, auth: firebaseAuth };
  } catch (error) {
    console.error('❌ Firebase initialization failed:', error);
    return { app: null, auth: null };
  }
}

/**
 * Get the Firebase Auth instance (initializes if needed)
 */
export function getFirebaseAuth(): Auth | null {
  if (!firebaseAuth) {
    const { auth } = initializeFirebase();
    return auth;
  }
  return firebaseAuth;
}

/**
 * Get the Firebase App instance (initializes if needed)
 */
export function getFirebaseApp(): FirebaseApp | null {
  if (!firebaseApp) {
    const { app } = initializeFirebase();
    return app;
  }
  return firebaseApp;
}

/**
 * Check if Firebase is available and enabled
 */
export function isFirebaseEnabled(): boolean {
  try {
    const config = getFirebaseConfig();
    return config !== null;
  } catch (error) {
    return false;
  }
}